import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { RunStatus, RunSummary } from "@canvas-sync/shared";
import { api } from "../api/client";
import { StatusBadge } from "../components/StatusBadge";
import { SubmissionBadge } from "../components/SubmissionBadge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TermDisplay } from "../components/TermBadge";
import type { ExpectedTerms } from "../lib/terms";
import {
  formatDateTime,
  formatDuration,
  formatIdLabel,
  formatRelative,
} from "../lib/format";
import {
  isSubmissionInFlight,
  runStatusPhrase,
  runSubmissionState,
  triggerLabel,
} from "../lib/labels";
import { POLL, usePoll } from "../lib/usePoll";
import { useRole } from "../lib/roleContext";
import { isRunStuck, stuckThresholdMinutes } from "../lib/runStatus";
import {
  computeInputMismatches,
  computeInputNovelty,
} from "../lib/inputComparison";

const NON_TERMINAL = new Set<RunStatus>(["QUEUED", "RUNNING"]);
const TERMINAL = new Set<RunStatus>(["SUCCEEDED", "FAILED", "CANCELLED"]);
const N8N_CADENCE_MS = 60 * 60 * 1000; // n8n cron (hourly) — see ScheduleCard.

export function DashboardPage() {
  const { isAdmin } = useRole();
  const [runs, setRuns] = useState<RunSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Admin-entered expected terms (Settings page) — drive the Term-column
  // highlight, same as the Syncs page. Best-effort: if the fetch fails the
  // column still renders, just without validation.
  const [expectedTerms, setExpectedTerms] = useState<ExpectedTerms>({});

  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then((s) => {
        if (!cancelled)
          setExpectedTerms({
            primary: s.expectedPrimaryTerm,
            secondary: s.expectedSecondaryTerm,
          });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  usePoll(
    async () => {
      try {
        const result = await api.listRuns(20);
        setRuns(result.runs);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      }
    },
    () =>
      runs &&
      runs.some(
        (r) =>
          NON_TERMINAL.has(r.status) ||
          isSubmissionInFlight(runSubmissionState(r)),
      )
        ? POLL.LIST_ACTIVE_MS
        : POLL.IDLE_MS,
  );

  // The list is already sorted queuedAt DESC by the API.
  const currentRun = runs?.find((r) => NON_TERMINAL.has(r.status));
  const lastSuccess = runs?.find((r) => r.status === "SUCCEEDED");
  const lastFailure = runs?.find((r) => r.status === "FAILED");
  const lastTerminal = runs?.find((r) => TERMINAL.has(r.status));
  const running = runs?.filter((r) => r.status === "RUNNING").length ?? 0;
  const totalGhosts = runs?.reduce((n, r) => n + (r.ghostsFound ?? 0), 0) ?? 0;
  // Runs that processed the SAME enrollment file as a prior run but found
  // MORE ghosts. Strict signal of a detection-logic gap — see
  // lib/inputComparison.ts for the rule.
  const inputMismatches = useMemo(
    () => (runs ? computeInputMismatches(runs) : new Set<string>()),
    [runs],
  );

  return (
    <section>
      <div className="page-head">
        <h1>Dashboard</h1>
        {isAdmin && (
          <Link to="/syncs/new" className="btn btn-primary">
            + Start new sync
          </Link>
        )}
      </div>

      <SyncStatusHero
        currentRun={currentRun}
        lastTerminal={lastTerminal}
        loaded={runs !== null}
      />

      <div className="stat-grid">
        <StatCard label="Recent syncs" value={runs?.length ?? "…"} />
        <StatCard
          label="Last successful sync"
          value={lastSuccess ? formatRelative(lastSuccess.finishedAt) : "—"}
          tone="success"
          to={lastSuccess ? `/syncs/${lastSuccess.runId}` : undefined}
        />
        <StatCard label="In progress" value={running} tone="info" />
        <StatCard
          label="Last failed sync"
          value={lastFailure ? formatRelative(lastFailure.finishedAt) : "—"}
          tone="danger"
          to={lastFailure ? `/syncs/${lastFailure.runId}` : undefined}
        />
        <StatCard
          label="Ghost enrollments found"
          value={totalGhosts}
          tone="warn"
        />
        <StatCard
          label="Inconsistent ghost counts"
          value={inputMismatches.size}
          tone={inputMismatches.size > 0 ? "danger" : undefined}
          to={inputMismatches.size > 0 ? "/syncs" : undefined}
        />
      </div>

      <ScheduleCard runs={runs ?? null} />

      <div className="card">
        <div className="card-head">
          <h2>Recent syncs</h2>
          <Link to="/syncs" className="muted-link">
            View all
          </Link>
        </div>
        {error && <div className="error">{error}</div>}
        {runs === null && !error && <div className="muted">Loading…</div>}
        {runs?.length === 0 && (
          <div className="muted">
            No syncs yet.
            {isAdmin && (
              <>
                {" "}
                <Link to="/syncs/new" className="muted-link">
                  Start one by uploading a CSV
                </Link>
                .
              </>
            )}
          </div>
        )}
        {runs && runs.length > 0 && (
          <RunsTable runs={runs.slice(0, 8)} expectedTerms={expectedTerms} />
        )}
      </div>
    </section>
  );
}

/**
 * Single prominent "what's happening right now" card. Three states:
 *   - A run is in flight → blue, links to that run.
 *   - Idle, last terminal was SUCCEEDED → green, relative time + link.
 *   - Idle, last terminal was FAILED/CANCELLED → red, prompts investigation.
 */
function SyncStatusHero({
  currentRun,
  lastTerminal,
  loaded,
}: {
  currentRun: RunSummary | undefined;
  lastTerminal: RunSummary | undefined;
  loaded: boolean;
}) {
  const { isAdmin } = useRole();
  if (!loaded) {
    return (
      <div className="hero hero-idle">
        <div className="hero-headline">Loading…</div>
      </div>
    );
  }
  if (!currentRun && !lastTerminal) {
    return (
      <div className="hero hero-idle">
        <div className="hero-headline">No syncs yet</div>
        {isAdmin && (
          <div className="hero-detail">
            <Link to="/syncs/new" className="muted-link">
              Start a sync
            </Link>{" "}
            by uploading a CSV.
          </div>
        )}
      </div>
    );
  }
  if (currentRun) {
    const startedAt = currentRun.queuedAt;
    const stuck = isRunStuck(currentRun);
    return (
      <Link
        to={`/syncs/${currentRun.runId}`}
        className={"hero hero-link " + (stuck ? "hero-warn" : "hero-running")}
      >
        <div className="hero-marker">
          {stuck ? null : <span className="badge-pulse" />}
        </div>
        <div>
          <div className="hero-headline">
            {stuck
              ? `This sync has been ${runStatusPhrase(currentRun.status)} for over ${stuckThresholdMinutes()} minutes — it may be stuck`
              : currentRun.status === "QUEUED"
                ? "A sync is waiting to start"
                : "A sync is in progress"}
          </div>
          <div className="hero-detail">
            <StatusBadge status={currentRun.status} /> started{" "}
            {formatRelative(startedAt)} ·{" "}
            <span className="small" title={currentRun.runId}>
              {formatIdLabel(currentRun.runId)}
            </span>
            {stuck && <span className="muted"> · click to investigate</span>}
          </div>
        </div>
      </Link>
    );
  }
  // Idle — show the most recent terminal outcome.
  const t = lastTerminal!;
  const succeeded = t.status === "SUCCEEDED";
  return (
    <Link
      to={`/syncs/${t.runId}`}
      className={"hero hero-link " + (succeeded ? "hero-ok" : "hero-bad")}
    >
      <div className="hero-marker" />
      <div>
        <div className="hero-headline">
          {succeeded
            ? `Last sync finished successfully ${formatRelative(t.finishedAt)}`
            : `Last sync ${runStatusPhrase(t.status)} ${formatRelative(t.finishedAt)}`}
        </div>
        <div className="hero-detail">
          {!succeeded && <span className="muted">Click to see why · </span>}
          <span className="small" title={t.runId}>
            {formatIdLabel(t.runId)}
          </span>
          {t.durationMs !== undefined && (
            <> · ran for {formatDuration(t.durationMs)}</>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * Schedule/trigger info. n8n is the authoritative scheduler today — this
 * card surfaces that fact and gives a heuristic "next expected" derived
 * from the most recent n8n-triggered run. If n8n's cadence ever changes,
 * update N8N_CADENCE_MS at the top of this file (and the wording below).
 */
function ScheduleCard({ runs }: { runs: RunSummary[] | null }) {
  const lastN8n = runs?.find((r) => r.trigger === "n8n");
  const nextEstimateMs = lastN8n
    ? new Date(lastN8n.queuedAt).getTime() + N8N_CADENCE_MS
    : null;
  const nextEstimate =
    nextEstimateMs !== null ? new Date(nextEstimateMs).toISOString() : null;
  const overdue =
    nextEstimateMs !== null && Date.now() > nextEstimateMs + 5 * 60 * 1000;

  return (
    <div className="card schedule-card">
      <div className="schedule-line">
        <span className="schedule-headline">
          Syncs run automatically every 60 minutes.
        </span>
      </div>
      <div className="muted small schedule-detail">
        {nextEstimate ? (
          <>
            Next scheduled sync: {formatDateTime(nextEstimate)}
            {overdue && (
              <span className="schedule-overdue">
                {" "}
                (overdue — the scheduler may not be running)
              </span>
            )}
          </>
        ) : (
          <>
            The next scheduled time will appear here after the first automatic
            sync runs.
          </>
        )}
        <details className="raw-details" style={{ marginTop: 8 }}>
          <summary>Show technical details</summary>
          <div className="raw-block" style={{ whiteSpace: "normal" }}>
            Scheduled by n8n every 60 minutes. n8n is the authoritative source —
            this next-time is a client-side estimate from the most recent
            n8n-triggered run.
          </div>
        </details>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
  to,
}: {
  label: string;
  value: string | number;
  tone?: "success" | "info" | "danger" | "warn";
  to?: string;
}) {
  const cls = "stat" + (tone ? ` stat-${tone}` : "") + (to ? " stat-link" : "");
  const body = (
    <>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </>
  );
  return to ? (
    <Link to={to} className={cls}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

type DashRun = RunSummary & { canvasEnv?: "prod" | "test" };

export function RunsTable({
  runs,
  expectedTerms = {},
}: {
  runs: DashRun[];
  expectedTerms?: ExpectedTerms;
}) {
  const { isAdmin } = useRole();
  const [submittingRun, setSubmittingRun] = useState<string | null>(null);
  const [submitErr, setSubmitErr] = useState<{
    runId: string;
    message: string;
  } | null>(null);
  // Warn (but allow) before re-posting a fix that's already in Canvas via a
  // SIS bundle — see RunsTimeline for the rationale.
  const [confirmRun, setConfirmRun] = useState<DashRun | null>(null);
  const nav = useNavigate();
  const mismatches = useMemo(() => computeInputMismatches(runs), [runs]);
  const novelty = useMemo(() => computeInputNovelty(runs), [runs]);

  function requestSubmit(r: DashRun) {
    if (r.sisBundleFixIncluded === true) setConfirmRun(r);
    else void handleSubmit(r.runId);
  }

  async function handleSubmit(runId: string) {
    setSubmitErr(null);
    setSubmittingRun(runId);
    try {
      const { response, canvasError } = await api.submitRunOutput(runId);
      if (canvasError) setSubmitErr({ runId, message: canvasError });
      nav(`/uploads/${response.uploadId}`);
    } catch (e) {
      setSubmitErr({ runId, message: (e as Error).message });
      setSubmittingRun(null);
    }
  }

  return (
    <>
      <table className="runs-table">
        <thead>
          <tr>
            <th>Sync ID</th>
            <th>Canvas</th>
            <th>Term</th>
            <th>Status</th>
            <th>Submission</th>
            <th>Started by</th>
            <th>Started</th>
            <th>Duration</th>
            <th>Ghosts</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => {
            const submission = runSubmissionState(r);
            const canSubmit =
              r.status === "SUCCEEDED" &&
              !r.latestUploadId &&
              (r.ghostsFound === undefined || r.ghostsFound > 0);
            const rowErr =
              submitErr?.runId === r.runId ? submitErr.message : null;
            return (
              <tr key={r.runId}>
                <td>
                  <Link
                    to={`/syncs/${r.runId}`}
                    className="run-link"
                    title={r.runId}
                  >
                    {formatIdLabel(r.runId)}
                  </Link>
                  <DashInputNoveltyBadge novelty={novelty.get(r.runId)} />
                </td>
                <td>
                  {r.canvasEnv ? (
                    <span className={`env-tag env-tag-${r.canvasEnv}`}>
                      {r.canvasEnv}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  <TermDisplay run={r} expectedTerms={expectedTerms} />
                </td>
                <td>
                  <StatusBadge status={r.status} />
                  {isRunStuck(r) && (
                    <span
                      className="stuck-tag"
                      title={`In progress for over ${stuckThresholdMinutes()} minutes`}
                    >
                      stuck?
                    </span>
                  )}
                </td>
                <td>
                  {r.latestUploadId ? (
                    <Link
                      to={`/uploads/${r.latestUploadId}`}
                      className="muted-link"
                      title={r.latestUploadId}
                    >
                      <SubmissionBadge state={submission} />
                    </Link>
                  ) : (
                    <SubmissionBadge state={submission} />
                  )}
                </td>
                <td>
                  <span className="trigger-pill">
                    {triggerLabel(r.trigger)}
                  </span>
                </td>
                <td className="muted">{formatRelative(r.queuedAt)}</td>
                <td className="mono">{formatDuration(r.durationMs)}</td>
                <td className="mono">
                  {r.ghostsFound ?? "—"}
                  {mismatches.has(r.runId) && (
                    <span
                      className="stuck-tag"
                      title="Found more ghosts than a prior run against the same enrollment file. Open the run to compare."
                      style={{ marginLeft: 6 }}
                    >
                      mismatch
                    </span>
                  )}
                </td>
                <td>
                  {isAdmin && canSubmit && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => requestSubmit(r)}
                      disabled={submittingRun === r.runId}
                      title={
                        r.sisBundleFixIncluded === true
                          ? "This run's fix was already shipped via a SIS bundle. Sending again will re-post it to Canvas."
                          : "Send the fix CSV to Canvas as a new upload"
                      }
                    >
                      {submittingRun === r.runId
                        ? "Sending…"
                        : "Send to Canvas"}
                    </button>
                  )}
                  {rowErr && (
                    <div className="error inline" style={{ marginTop: 4 }}>
                      {rowErr}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {confirmRun && (
        <ConfirmDialog
          title="Fix already shipped via SIS bundle"
          message={
            `This sync's ghost-fix CSV was already sent to Canvas inside SIS bundle ` +
            `${confirmRun.sisBundleId ?? "(unknown)"}. Sending it again will re-post the ` +
            `same deletions as a separate SIS import. Only do this if you intend to re-apply it.`
          }
          confirmPhrase="submit again"
          confirmLabel="Send to Canvas anyway"
          cancelLabel="Cancel"
          danger
          busy={submittingRun === confirmRun.runId}
          onConfirm={() => {
            const runId = confirmRun.runId;
            setConfirmRun(null);
            void handleSubmit(runId);
          }}
          onCancel={() => setConfirmRun(null)}
        />
      )}
    </>
  );
}

/**
 * Inline pill next to the Sync ID indicating whether this run's
 * enrollment file is fresh from PS or a repeat of one we've processed
 * before. Same logic as RunsTimeline's InputNoveltyBadge — see
 * lib/inputComparison.ts for the rule.
 */
function DashInputNoveltyBadge({
  novelty,
}: {
  novelty: { isNew: boolean; priorCount: number; known: boolean } | undefined;
}) {
  if (!novelty || !novelty.known) return null;
  if (novelty.isNew) {
    return (
      <span
        className="badge badge-success"
        style={{ marginLeft: 6, fontSize: 10 }}
        title="First run we've seen for this enrollment file (md5)"
      >
        new input
      </span>
    );
  }
  const occurrence = novelty.priorCount + 1;
  const suffix =
    occurrence === 1
      ? "st"
      : occurrence === 2
        ? "nd"
        : occurrence === 3
          ? "rd"
          : "th";
  return (
    <span
      className="badge badge-cancelled"
      style={{ marginLeft: 6, fontSize: 10 }}
      title={`This enrollment file has been processed ${novelty.priorCount} time${novelty.priorCount === 1 ? "" : "s"} before — current run is the ${occurrence}${suffix} occurrence`}
    >
      repeat ({occurrence}
      {suffix})
    </span>
  );
}
